import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faLock, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { rolesService, type Role } from '@/services/roles.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { isForbiddenError } from '@/lib/is-forbidden-error';

function RoleCard({ role }: { role: Role }) {
  const [expanded, setExpanded] = useState(false);
  const hasWildcard = role.permissions.includes('*');

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{role.name}</span>
              {role.isSystem && (
                <FontAwesomeIcon icon={faLock} className="h-3 w-3 text-muted-foreground" title="System role" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{role.description ?? role.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          {hasWildcard ? (
            <span className="inline-flex items-center rounded-full bg-accent/20 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
              Full Access
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">{role.permissions.length} permissions</span>
          )}
          {!hasWildcard && role.permissions.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={expanded ? 'Collapse' : 'Expand'}
              aria-expanded={expanded}
            >
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {expanded && !hasWildcard && role.permissions.length > 0 && (
        <div className="border-t border-border px-5 py-3">
          <div className="flex flex-wrap gap-1.5">
            {role.permissions.map((p) => (
              <span key={p} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RolesPage() {
  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesService.listAll,
  });

  if (isLoading) return <SkeletonPage />;
  // Audit F-2 — a refusal is not an absence. Without this a 403 falls through
  // to the empty state, which reads as a fact about the tenant.
  if (isForbiddenError(error)) return <AccessRestricted />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Roles"
        description="System roles and their permission sets — read only"
      />

      {roles && roles.length > 0 ? (
        <div className="space-y-3">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={faShieldHalved}
          title="No roles found"
          description="Roles are provisioned automatically when a tenant is created."
        />
      )}
    </div>
  );
}
